// NOC-CC/frontend/src/lib/soc-api-client.ts
// SOC API Client — routes to SOC Agent backend (port 8002).
// Shares the token cache from noc-api-client so we never duplicate session fetches.

import { acquireToken, invalidateTokenCache, forceReauth } from './noc-api-client'

const isAuthDebug = process.env.NEXT_PUBLIC_SUPERVITY_AUTH_DEBUG === 'true'

function log(msg: string) {
  if (isAuthDebug) console.log(`[SOC API Client] ${new Date().toISOString()}: ${msg}`)
}

function decodeTokenForLogging(token: string): string {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    const now = Math.floor(Date.now() / 1000)
    const expDelta = payload.exp ? payload.exp - now : null
    return (
      `iss="${payload.iss}" aud="${payload.aud}" azp="${payload.azp}" sub="${payload.sub}" ` +
      `exp=${payload.exp} (${expDelta != null ? (expDelta > 0 ? expDelta + 's left' : 'EXPIRED ' + Math.abs(expDelta) + 's ago') : '?'})`
    )
  } catch {
    return '<could not decode>'
  }
}

const getSocApiUrl = (): string => {
  if (process.env.NEXT_PUBLIC_SOC_API_URL) return process.env.NEXT_PUBLIC_SOC_API_URL
  if (typeof window !== 'undefined') return window.location.origin
  return 'http://localhost:8002'
}

const BASE_PATH = process.env.NEXT_PUBLIC_SOC_BASE_PATH || process.env.NEXT_PUBLIC_BASE_PATH || ''

async function apiClient(endpoint: string, options: RequestInit = {}) {
  const token = await acquireToken()
  if (!token) {
    log(`No token for ${endpoint} — forcing re-authentication`)
    await forceReauth()
    throw new Error('No active session. Please sign in.')
  }

  log(`→ ${endpoint} | Token: ${decodeTokenForLogging(token)}`)

  const headers = new Headers(options.headers || {})
  headers.set('Authorization', `Bearer ${token}`)

  const fullUrl = `${getSocApiUrl()}${BASE_PATH}${endpoint}`
  const response = await fetch(fullUrl, { ...options, headers })

  if (response.status === 401) {
    const errBody = await response.text().catch(() => '')
    log(`🔴 401 on ${endpoint} — backend said: ${errBody}`)
    log(`   Token was: ${decodeTokenForLogging(token)}`)
    log(`   Invalidating cache, fetching fresh token…`)
    invalidateTokenCache()

    const freshToken = await acquireToken()
    if (freshToken && freshToken !== token) {
      log(`Retrying ${endpoint} with refreshed token`)
      const retryHeaders = new Headers(options.headers || {})
      retryHeaders.set('Authorization', `Bearer ${freshToken}`)
      const retry = await fetch(fullUrl, { ...options, headers: retryHeaders })
      if (retry.ok || retry.status === 204) {
        return retry.status === 204 ? null : retry.json()
      }
    }

    log(`Token invalid after refresh — forcing re-authentication`)
    invalidateTokenCache()
    await forceReauth()
    throw new Error('Session expired. Redirecting to sign in.')
  }

  if (response.status === 409) {
    const err = await response.json().catch(() => ({ detail: 'Resource is currently locked.' }))
    throw new Error(err.detail)
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: response.statusText }))
    throw new Error(err.detail || 'An API error occurred.')
  }

  if (response.status === 204) return null
  return response.json()
}

const get    = (ep: string, opts?: RequestInit) => apiClient(ep, { ...opts, method: 'GET' })
const post   = (ep: string, data?: unknown, opts?: RequestInit) => apiClient(ep, { ...opts, method: 'POST', headers: { 'Content-Type': 'application/json', ...opts?.headers as Record<string, string> }, body: data ? JSON.stringify(data) : undefined })
const patch  = (ep: string, data?: unknown, opts?: RequestInit) => apiClient(ep, { ...opts, method: 'PATCH', headers: { 'Content-Type': 'application/json', ...opts?.headers as Record<string, string> }, body: data ? JSON.stringify(data) : undefined })
const put    = (ep: string, data?: unknown, opts?: RequestInit) => apiClient(ep, { ...opts, method: 'PUT', headers: { 'Content-Type': 'application/json', ...opts?.headers as Record<string, string> }, body: data ? JSON.stringify(data) : undefined })
const del    = (ep: string, opts?: RequestInit) => apiClient(ep, { ...opts, method: 'DELETE' })

export default apiClient
export const socApi = { get, post, patch, put, delete: del }

// Typed suspension event helpers
export interface SuspensionEntity {
  id: string
  name: string
  type: 'agent' | 'employee'
  result: string
  actions: { op: string; result: string; error?: string }[]
  verification?: {
    agent_active: boolean
    credential_active: boolean
    verified: boolean
    agent_status: string
  }
}

export interface SuspensionEvent {
  id: number
  action: string
  scope: string
  triggered_by: string
  trigger_source: string
  email_summary: string | null
  affected_entities: SuspensionEntity[]
  triggered_at: string
  resumed_at: string | null
  resumed_by: string | null
}

export const suspensionApi = {
  list: (limit = 50): Promise<SuspensionEvent[]> =>
    get(`/api/soc/suspension-events?limit=${limit}`),
  suspend: (scope: string, reason?: string) =>
    post('/api/soc/suspend-noc', { scope, reason }),
  resume: (eventId: number) =>
    post(`/api/soc/suspension-events/${eventId}/resume`, {}),
  verify: (eventId: number) =>
    get(`/api/soc/suspension-events/${eventId}/verify`),
}
